// Static-build API client — no server calls.
// All product/category data comes from the baked JSON. Orders live in localStorage.
//
// This file replaces the axios-based network client so the site can be uploaded
// to any static/public_html host and remain fully functional (browse, filter,
// search, cart, mock checkout, order confirmation, recently-viewed, orders).

import DATA from "../data/products.json";

const PRODUCTS = DATA.products;
const CATEGORIES = DATA.categories;

const ORDER_STORAGE_KEY = "brokeshopping.serverOrders.v1";
// Small artificial latency so UI loading states still animate nicely.
const DELAY_MS = 60;

function pause(ms = DELAY_MS) {
  return new Promise((r) => setTimeout(r, ms));
}

function readOrders() {
  try {
    const raw = localStorage.getItem(ORDER_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeOrders(map) {
  try {
    localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* quota — ignore */
  }
}

// ---------- API-shape functions ---------- //

export const listCategories = async () => {
  await pause();
  return CATEGORIES;
};

export const listProducts = async (params = {}) => {
  await pause();
  const {
    category,
    search,
    min_price,
    max_price,
    sort = "featured",
    skip = 0,
    limit = 24,
  } = params;

  let items = PRODUCTS;

  if (category) {
    items = items.filter((p) => p.category === category);
  }
  if (search) {
    const q = String(search).toLowerCase();
    items = items.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q) ||
        (p.categoryLabel || "").toLowerCase().includes(q)
    );
  }
  if (typeof min_price === "number") {
    items = items.filter((p) => Number(p.price) >= min_price);
  }
  if (typeof max_price === "number") {
    items = items.filter((p) => Number(p.price) <= max_price);
  }

  const sorters = {
    price_asc: (a, b) => a.price - b.price,
    price_desc: (a, b) => b.price - a.price,
    rating: (a, b) => b.rating - a.rating,
    newest: (a, b) => String(b.createdAt).localeCompare(String(a.createdAt)),
    featured: (a, b) => b.rating - a.rating || b.reviewCount - a.reviewCount,
  };
  const cmp = sorters[sort] || sorters.featured;
  items = [...items].sort(cmp);

  const total = items.length;
  const paged = items.slice(skip, skip + limit).map((p) => {
    // Match backend behavior of dropping full `reviews` from list responses.
    const { reviews, ...rest } = p;
    return rest;
  });
  return { items: paged, total, skip, limit };
};

export const getProduct = async (id) => {
  await pause();
  const p = PRODUCTS.find((x) => x.id === id);
  if (!p) throw new Error("Product not found");
  return p;
};

export const getHomeFeatured = async () => {
  await pause();
  const out = {};
  for (const cat of CATEGORIES) {
    const items = PRODUCTS.filter((p) => p.category === cat.slug)
      .sort((a, b) => b.rating - a.rating || b.reviewCount - a.reviewCount)
      .slice(0, 4)
      .map(({ reviews, ...rest }) => rest);
    out[cat.slug] = { label: cat.label, tagline: cat.tagline, items };
  }
  return out;
};

function makeOrderNumber() {
  const d = new Date();
  const yy = String(d.getUTCFullYear()).slice(2);
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const tail = Math.random().toString(16).slice(2, 8).toUpperCase();
  return `BS-${yy}${mm}${dd}-${tail}`;
}

export const createOrder = async (payload) => {
  await pause();
  const order = {
    id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now()),
    orderNumber: makeOrderNumber(),
    ...payload,
    status: "confirmed",
    createdAt: new Date().toISOString(),
  };
  const map = readOrders();
  map[order.orderNumber] = order;
  writeOrders(map);
  return order;
};

export const getOrder = async (orderNumber) => {
  await pause();
  const map = readOrders();
  const order = map[orderNumber];
  if (!order) throw new Error("Order not found");
  return order;
};

// Kept for backwards compatibility with any lingering imports.
export const API = "";
export const api = {};
